// Live chat-model catalog for the AI Assistant pickers.
//
// GET /api/ai-assistant/models/ asks each connected provider for its newest
// models (see backend/api/llm_models.py), so a new release shows up in the
// picker without a frontend change. Nothing is hardcoded here: when the
// endpoint cannot be reached the picker shows the error instead of a list.
//
// A stored legacy numeric choice (0/1/2) is shown as the newest model of that
// line (haiku / sonnet / opus) via the backend's `legacy_chat_models` map, and
// a real model id is stored as soon as the user picks anything.

export function isChatModelId(value) {
  return typeof value === "string" && /^claude/i.test(value.trim());
}

// Loose request/DB value → int (legacy) | string (model id) | null.
export function coerceChatModel(value) {
  if (value == null || typeof value === "boolean") return null;
  if (typeof value === "number") return Number.isInteger(value) ? value : null;
  if (typeof value !== "string") return null;
  const s = value.trim();
  if (s === "") return null;
  if (/^-?\d+$/.test(s)) return parseInt(s, 10);
  if (isChatModelId(s)) return s;
  return null;
}

function lineOf(model) {
  const line = (model.line || "").toLowerCase();
  if (line) return line;
  const id = (model.model_id || "").toLowerCase();
  if (id.includes("haiku")) return "haiku";
  if (id.includes("opus")) return "opus";
  if (id.includes("sonnet")) return "sonnet";
  return "";
}

const LINE_HINTS = { haiku: "Fastest", sonnet: "Balanced", opus: "Most capable" };

export function chatModelLabel(model) {
  const hint = LINE_HINTS[lineOf(model)];
  return hint ? `${model.display_name} (${hint})` : model.display_name;
}

export function humanizeModelId(modelId) {
  const clean = String(modelId || "")
    .replace(/-\d{8}$/, "")
    .replace(/-(\d+)-(\d+)$/, " $1.$2");
  return clean
    .split(/[-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// The model id a stored chatModel value points at, or null when it is a
// legacy int and the live map is not loaded yet.
export function resolveChatModelId(value, legacyMap) {
  const c = coerceChatModel(value);
  if (typeof c === "string") return c;
  const map = legacyMap || {};
  const key = typeof c === "number" ? c : 1;
  return map[key] || map[String(key)] || null;
}

// Options for a <select>: the live list, plus the current selection if it is a
// real id that is no longer among the newest (so the control never goes blank).
export function chatModelOptions(models, currentValue, legacyMap) {
  const list = Array.isArray(models) ? models : [];
  const options = list.map((m) => ({ value: m.model_id, label: chatModelLabel(m) }));
  let current = resolveChatModelId(currentValue, legacyMap);
  if (current && !options.some((o) => o.value === current)) {
    options.unshift({ value: current, label: `${humanizeModelId(current)} (current)` });
  }
  if (!current) current = options.length ? options[0].value : "";
  return { options, current };
}

// Display name for a chat answer header / toast.
export function describeChatModel(value, models, legacyMap) {
  const id = resolveChatModelId(value, legacyMap);
  if (!id) return "";
  const hit = (models || []).find((m) => m.model_id === id);
  return hit ? hit.display_name : humanizeModelId(id);
}

const EMPTY = { chatModels: [], legacyMap: null, providers: {}, models: [], ocrModels: [], defaultOcrModels: [], fetchedAt: null };

// Fetch the live list. Never throws: on failure returns ok:false + error and
// empty lists (no hardcoded stand-ins).
export async function fetchChatModels(backendBaseUrl, options = {}) {
  const base = String(backendBaseUrl || "").replace(/\/+$/, "");
  const qs = options.refresh ? "?refresh=1" : "";
  try {
    const res = await fetch(`${base}/api/ai-assistant/models/${qs}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const chatModels = Array.isArray(data?.chat_models) ? data.chat_models : [];
    if (!chatModels.length) {
      const detail = data?.providers?.anthropic?.error || data?.error;
      throw new Error(detail ? `no chat models (${detail})` : "no chat models in response");
    }
    return {
      ok: true,
      error: null,
      chatModels,
      legacyMap: data.legacy_chat_models || null,
      providers: data.providers || {},
      models: Array.isArray(data.models) ? data.models : [],
      ocrModels: Array.isArray(data.ocr_models) ? data.ocr_models : [],
      defaultOcrModels: Array.isArray(data.default_ocr_models) ? data.default_ocr_models : [],
      fetchedAt: data.fetched_at || null,
    };
  } catch (error) {
    return { ...EMPTY, ok: false, error: error?.message || String(error) };
  }
}

// OCR priority defaults from the live AgenticAI list: the newest model of each
// provider, Google → OpenAI → Anthropic, filled to three from the rest. Returns
// [] when nothing has loaded (callers tell the user instead of guessing).
export function defaultOcrModelsFrom(availableModels) {
  const order = ["google", "openai", "anthropic"];
  const picks = [];
  for (const provider of order) {
    const hit = (availableModels || []).find((m) => m?.config?.provider === provider);
    if (hit?.model_id) picks.push(hit.model_id);
  }
  for (const m of availableModels || []) {
    if (picks.length >= 3) break;
    if (m?.model_id && !picks.includes(m.model_id)) picks.push(m.model_id);
  }
  return picks;
}
